import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { calendarApi } from '@/api/endpoints/calendar'
import type { CalendarEvent } from '@/types'
import type { CalendarQueryParams, CreateEventData, UpdateEventData } from '@/api/endpoints/calendar'

// ===== Calendar events list =====

export function useCalendarEvents(params: CalendarQueryParams) {
  const query = useQuery({
    queryKey: ['calendar', params],
    queryFn: async () => {
      try {
        const res = await calendarApi.getEvents(params)
        return res.data
      } catch {
        return {
          data: [] as CalendarEvent[],
          total: 0,
          page: 1,
          pageSize: params.size ?? 20,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        }
      }
    },
    staleTime: 30_000,
  })

  return {
    events: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    totalPages: query.data?.totalPages ?? 0,
    hasNext: query.data?.hasNext ?? false,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

// ===== Single calendar event =====

export function useCalendarEvent(id: string, options?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: ['calendar', id],
    queryFn: async () => {
      const res = await calendarApi.getEvent(id)
      return res.data
    },
    enabled: options?.enabled !== false && Boolean(id),
    staleTime: 30_000,
  })

  return {
    event: query.data as CalendarEvent | undefined,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

// ===== Create event =====

export function useCreateEvent() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (data: CreateEventData) => calendarApi.createEvent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar'] })
      queryClient.invalidateQueries({ queryKey: ['meetings'] })
    },
  })

  return {
    createEvent: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}

// ===== Update event =====

export function useUpdateEvent(id: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (data: UpdateEventData) => calendarApi.updateEvent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar', id] })
      queryClient.invalidateQueries({ queryKey: ['calendar'] })
      queryClient.invalidateQueries({ queryKey: ['meetings'] })
    },
  })

  return {
    updateEvent: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}

// ===== Delete event =====

export function useDeleteEvent() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (id: string) => calendarApi.deleteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar'] })
      queryClient.invalidateQueries({ queryKey: ['meetings'] })
    },
  })

  return {
    deleteEvent: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}

// ===== Save meeting minutes =====

export function useSaveMeetingMinutes(eventId: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (data: { content: string; actionItems: string[] }) =>
      calendarApi.saveMeetingMinutes(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar', eventId] })
      queryClient.invalidateQueries({ queryKey: ['calendar'] })
    },
  })

  return {
    saveMinutes: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}
